import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock,
  Search,
  Filter,
  ArrowUpRight,
  Users,
  TimerReset,
  CheckCircle2,
  Layers,
  TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// Existing metrics you already have
import SampleOrderCostTimeFrame from "../metrics/SampleOrderCostTimeFrame";
import SampleOrderCostPerUser from "../metrics/SampleOrderCostPerUser";

const STATUS_ORDER = ["Pending", "Approved", "Rejected", "Completed", "Converted to Order"];
const CHART_COLORS = ["#2563eb", "#7c3aed", "#0f766e", "#d97706", "#dc2626", "#475569"];

/**
 * SampleOrdersAnalytics
 * - Uses statusEnteredAt/statusExitedAt (if present) to calculate durations
 * - Falls back to updatedAt/createdAt when status timestamps are missing
 */
const SampleOrdersAnalytics = ({
  companyId,
  sampleOrders = [],
  companyUsers = [],
  accounts = [],
  isAdmin = false,
  currentUserId = null,
  companySettings = null,
  onSelectRequest = null,
}) => {
  const [timeframe, setTimeframe] = useState("30"); // "7" | "30" | "90" | "all"
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [assigneeId, setAssigneeId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const getDefaultThresholdDays = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    if (!normalized) return 3;
    if (normalized.includes("pending")) return 2;
    if (normalized.includes("approved")) return 2;
    if (normalized.includes("follow")) return 3;
    if (normalized.includes("deliver")) return 3;
    if (
      normalized.includes("complete") ||
      normalized.includes("reject") ||
      normalized.includes("cancel") ||
      normalized.includes("convert")
    ) {
      return null;
    }
    return 3;
  };

  const [thresholds, setThresholds] = useState({
    Pending: 2,
    Approved: 2,
    Delivered: 3,
    "Follow Up": 3,
  });

  const keyify = (status) =>
    String(status || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[./#[\]$]/g, "_");

  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (ts instanceof Date) return ts;
    return null;
  };

  const parseYmd = (ymd) => {
    if (!ymd || typeof ymd !== "string") return null;
    // Expect "YYYY-MM-DD"
    const d = new Date(`${ymd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const isClosedStatus = (status) => {
    const s = String(status || "").toLowerCase();
    return (
      s.includes("complete") ||
      s.includes("rejected") ||
      s.includes("cancel") ||
      s.includes("converted")
    );
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const fmtDuration = (ms) => {
    if (!Number.isFinite(ms) || ms <= 0) return "—";
    const totalMin = Math.floor(ms / 60000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin - days * 60 * 24) / 60);
    const mins = totalMin - days * 60 * 24 - hours * 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const avg = (arr) => {
    if (!arr.length) return null;
    return arr.reduce((a, b) => a + b, 0) / arr.length;
  };

  const median = (arr) => {
    if (!arr.length) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const now = useMemo(() => new Date(), []);

  const usersById = useMemo(() => {
    const map = {};
    companyUsers.forEach((u) => (map[u.id] = u));
    return map;
  }, [companyUsers]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => (map[a.id] = a));
    return map;
  }, [accounts]);

  const enriched = useMemo(() => {
    return (Array.isArray(sampleOrders) ? sampleOrders : []).map((r) => {
      const status = r.status || "Unknown";

      const createdAt = toDateSafe(r.createdAt) || null;
      const updatedAt = toDateSafe(r.updatedAt) || null;

      // statusEnteredAt may be stored with raw key or keyified key
      const enteredMap = r.statusEnteredAt || {};
      const exitedMap = r.statusExitedAt || {};

      const enteredTs =
        enteredMap?.[status] ||
        enteredMap?.[keyify(status)] ||
        null;

      const enteredAt = toDateSafe(enteredTs) || updatedAt || createdAt;

      const ageInStatusMs =
        enteredAt && !Number.isNaN(enteredAt.getTime())
          ? now.getTime() - enteredAt.getTime()
          : null;

      const reqDelivery = parseYmd(r.requestedDate) || null;

      const assignedToId = r.assignedToId || null;
      const assignedToName =
        r.assignedToName ||
        usersById[assignedToId]?.fullName ||
        usersById[assignedToId]?.email ||
        null;

      const accountId = r.accountId || null;
      const accountName =
        r.accountName ||
        accountsById[accountId]?.name ||
        null;

      const closed = isClosedStatus(status);

      // Cycle time (best effort)
      let closedAt = null;
      if (closed) {
        // Prefer lastStatusChangeAt or updatedAt
        closedAt =
          toDateSafe(r.lastStatusChangeAt) ||
          updatedAt ||
          enteredAt ||
          null;
      }
      const cycleTimeMs =
        createdAt && closedAt ? closedAt.getTime() - createdAt.getTime() : null;

      // Overdue logic: requestedDate in the past AND not closed
      const overdue =
        !!reqDelivery && reqDelivery.getTime() < now.getTime() && !closed;

      // "Stuck" logic: compare against per-status threshold (fallback 3 days)
      const threshDays =
        thresholds[status] ??
        thresholds[keyify(status)] ??
        getDefaultThresholdDays(status);

      const stuck =
        !closed &&
        threshDays != null &&
        Number.isFinite(ageInStatusMs) &&
        ageInStatusMs > threshDays * 24 * 60 * 60 * 1000;

      return {
        ...r,
        _status: status,
        _createdAt: createdAt,
        _updatedAt: updatedAt,
        _enteredAt: enteredAt,
        _ageInStatusMs: ageInStatusMs,
        _cycleTimeMs: cycleTimeMs,
        _closed: closed,
        _overdue: overdue,
        _stuck: stuck,
        _assignedToId: assignedToId,
        _assignedToName: assignedToName,
        _accountName: accountName,
        _requestedDelivery: reqDelivery,
        _enteredMap: enteredMap,
        _exitedMap: exitedMap,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleOrders, usersById, accountsById, thresholds]);

  const filtered = useMemo(() => {
    let list = [...enriched];

    // timeframe filter by createdAt
    if (timeframe !== "all") {
      const days = Number(timeframe);
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      cutoff.setHours(0, 0, 0, 0); // Start of day for clean cutoff
      list = list.filter((r) => {
        if (!r._createdAt) return false; // Exclude records without created date
        return r._createdAt >= cutoff;
      });
    }

    if (onlyOpen) list = list.filter((r) => !r._closed);

    if (assigneeId !== "all") {
      if (assigneeId === "unassigned") list = list.filter((r) => !r._assignedToId);
      else list = list.filter((r) => r._assignedToId === assigneeId);
    }

    if (statusFilter !== "all") list = list.filter((r) => r._status === statusFilter);

    const query = String(q || "").trim().toLowerCase();
    if (query) {
      list = list.filter((r) => {
        const hay = [
          r._accountName,
          r.productName,
          r.notes,
          r._status,
          r._assignedToName,
          r.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      });
    }

    return list;
  }, [enriched, timeframe, onlyOpen, assigneeId, statusFilter, q, now]);

  const allStatuses = useMemo(() => {
    const set = new Set(enriched.map((r) => r._status || "Unknown"));
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a);
      const bi = STATUS_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a).localeCompare(String(b));
    });
    return arr;
  }, [enriched]);

  const editableStatuses = useMemo(
    () => allStatuses.filter((status) => !isClosedStatus(status)),
    [allStatuses]
  );

  const kpis = useMemo(() => {
    const base = filtered;
    const open = base.filter((r) => !r._closed);
    const overdue = open.filter((r) => r._overdue);
    const stuck = open.filter((r) => r._stuck);

    const ages = open.map((r) => r._ageInStatusMs).filter((x) => Number.isFinite(x));
    const closed = base.filter((r) => r._closed);
    const cycles = closed.map((r) => r._cycleTimeMs).filter((x) => Number.isFinite(x));

    return {
      openCount: open.length,
      overdueCount: overdue.length,
      stuckCount: stuck.length,
      avgAgeMs: avg(ages),
      medAgeMs: median(ages),
      avgCycleMs: avg(cycles),
      medCycleMs: median(cycles),
    };
  }, [filtered]);

  const kitUsage = useMemo(() => {
    const kitOrders = filtered.filter((r) => !!r.kitId);
    const convertedKitOrders = kitOrders.filter((r) => {
      const status = String(r._status || r.status || "").toLowerCase();
      return status.includes("converted") || r.convertedToOrder === true || !!r.orderNumber;
    });

    const uniqueKitIds = new Set(kitOrders.map((r) => r.kitId).filter(Boolean));
    const convertedKitIds = new Set(convertedKitOrders.map((r) => r.kitId).filter(Boolean));

    return {
      usageCount: kitOrders.length,
      convertedCount: convertedKitOrders.length,
      uniqueKitCount: uniqueKitIds.size,
      convertedKitCount: convertedKitIds.size,
      conversionPct: kitOrders.length ? Math.round((convertedKitOrders.length / kitOrders.length) * 100) : 0,
    };
  }, [filtered]);

  const statusBuckets = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const s = r._status || "Unknown";
      map[s] = map[s] || [];
      map[s].push(r);
    });
    const rows = Object.entries(map).map(([status, items]) => {
      const ages = items.map((i) => i._ageInStatusMs).filter((x) => Number.isFinite(x));
      return {
        status,
        count: items.length,
        medAgeMs: median(ages),
        avgAgeMs: avg(ages),
      };
    });

    rows.sort((a, b) => (b.count || 0) - (a.count || 0));
    return rows;
  }, [filtered]);

  const activitySeries = useMemo(() => {
    const base = filtered
      .filter((r) => r._createdAt instanceof Date && !Number.isNaN(r._createdAt.getTime()))
      .sort((a, b) => a._createdAt - b._createdAt);

    if (!base.length) return [];

    const useWeeklyBuckets = timeframe === "90" || timeframe === "all";
    const map = new Map();

    const getBucketKey = (sourceDate) => {
      const d = new Date(sourceDate);
      if (useWeeklyBuckets) {
        const day = d.getDay();
        const diff = (day + 6) % 7;
        d.setDate(d.getDate() - diff);
      }
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const ensureBucket = (bucketDate) => {
      const key = bucketDate.toISOString();
      const existing = map.get(key) || {
        label: bucketDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        created: 0,
        delivered: 0,
        completed: 0,
      };
      map.set(key, existing);
      return existing;
    };

    base.forEach((row) => {
      const createdBucket = getBucketKey(row._createdAt);
      ensureBucket(createdBucket).created += 1;

      const deliveredAt = parseYmd(row.deliveryDate);
      if (deliveredAt) {
        const deliveredBucket = getBucketKey(deliveredAt);
        ensureBucket(deliveredBucket).delivered += 1;
      }

      const statusLower = String(row._status || "").toLowerCase();
      if (statusLower.includes("complete") || statusLower.includes("convert")) {
        const completedAt =
          toDateSafe(row.lastStatusChangeAt) ||
          row._updatedAt ||
          row._enteredAt ||
          null;
        if (completedAt) {
          const completedBucket = getBucketKey(completedAt);
          ensureBucket(completedBucket).completed += 1;
        }
      }
    });

    return Array.from(map.entries())
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([, value]) => value);
  }, [filtered, timeframe]);

  const activitySnapshot = useMemo(() => {
    return activitySeries.reduce(
      (acc, row) => {
        acc.created += row.created || 0;
        acc.delivered += row.delivered || 0;
        acc.completed += row.completed || 0;
        return acc;
      },
      { created: 0, delivered: 0, completed: 0 }
    );
  }, [activitySeries]);

  const accountChartData = useMemo(() => {
    const map = new Map();

    filtered.forEach((row) => {
      const key = row._accountName || "Unassigned account";
      const status = String(row._status || "").toLowerCase();
      const current = map.get(key) || {
        name: key,
        total: 0,
        open: 0,
        completed: 0,
        converted: 0,
      };

      current.total += 1;

      if (!row._closed) current.open += 1;
      if (status.includes("complete")) current.completed += 1;
      if (status.includes("convert") || row.convertedToOrder === true || !!row.orderNumber) {
        current.converted += 1;
      }

      map.set(key, current);
    });

    return Array.from(map.values())
      .sort((a, b) => {
        if ((b.total || 0) !== (a.total || 0)) return (b.total || 0) - (a.total || 0);
        return String(a.name).localeCompare(String(b.name));
      })
      .slice(0, 8)
      .reverse();
  }, [filtered]);

  const statusChartData = useMemo(
    () => statusBuckets.map((row) => ({ name: row.status, value: row.count || 0 })),
    [statusBuckets]
  );

  const bottleneck = useMemo(() => {
    if (!statusBuckets.length) return null;
    // Pick the status with the largest median age
    const ranked = [...statusBuckets].sort((a, b) => (b.medAgeMs || 0) - (a.medAgeMs || 0));
    return ranked[0] || null;
  }, [statusBuckets]);

  const stuckOrders = useMemo(() => {
    const list = filtered
      .filter((r) => r._stuck || r._overdue)
      .sort((a, b) => (b._ageInStatusMs || 0) - (a._ageInStatusMs || 0))
      .slice(0, 20);
    return list;
  }, [filtered]);

  const perAssignee = useMemo(() => {
    const open = filtered.filter((r) => !r._closed);
    const map = {};

    open.forEach((r) => {
      const id = r._assignedToId || "unassigned";
      map[id] = map[id] || {
        id,
        name:
          id === "unassigned"
            ? "Unassigned"
            : usersById[id]?.fullName || usersById[id]?.email || id,
        openCount: 0,
        overdueCount: 0,
        stuckCount: 0,
        ages: [],
      };
      map[id].openCount += 1;
      if (r._overdue) map[id].overdueCount += 1;
      if (r._stuck) map[id].stuckCount += 1;
      if (Number.isFinite(r._ageInStatusMs)) map[id].ages.push(r._ageInStatusMs);
    });

    const rows = Object.values(map).map((x) => ({
      ...x,
      medAgeMs: median(x.ages),
    }));

    rows.sort((a, b) => (b.openCount || 0) - (a.openCount || 0));
    return rows;
  }, [filtered, usersById]);

  const timeSpentByStatus = useMemo(() => {
    // Uses statusEnteredAt + statusExitedAt if present
    // Aggregates durations for statuses where we have both timestamps
    const durationsByStatus = {}; // status -> [ms, ms, ...]
    filtered.forEach((r) => {
      const entered = r._enteredMap || {};
      const exited = r._exitedMap || {};

      const statuses = new Set([
        ...Object.keys(entered || {}),
        ...Object.keys(exited || {}),
      ]);

      statuses.forEach((k) => {
        const en = toDateSafe(entered[k]);
        const ex = toDateSafe(exited[k]);
        if (!en || !ex) return;
        const ms = ex.getTime() - en.getTime();
        if (!Number.isFinite(ms) || ms <= 0) return;
        durationsByStatus[k] = durationsByStatus[k] || [];
        durationsByStatus[k].push(ms);
      });
    });

    const rows = Object.entries(durationsByStatus).map(([statusKey, arr]) => ({
      statusKey,
      avgMs: avg(arr),
      medMs: median(arr),
      samples: arr.length,
    }));

    rows.sort((a, b) => (b.medMs || 0) - (a.medMs || 0));
    return rows;
  }, [filtered]);

  const maxBar = useMemo(() => {
    const maxCount = Math.max(...statusBuckets.map((s) => s.count || 0), 1);
    return maxCount;
  }, [statusBuckets]);

  const Card = ({ icon: Icon, title, value, sub, tone = "slate" }) => {
    const toneMap = {
      slate: "border-slate-200 bg-white",
      red: "border-red-200 bg-red-50",
      amber: "border-amber-200 bg-amber-50",
      blue: "border-blue-200 bg-blue-50",
      green: "border-green-200 bg-green-50",
    };
    return (
      <div className={`rounded-2xl border ${toneMap[tone] || toneMap.slate} p-4 shadow-sm`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
            {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
          </div>
          {Icon ? (
            <div className="p-2 rounded-xl bg-white/70 border border-slate-200">
              <Icon className="w-5 h-5 text-slate-700" />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Sample Order Analytics
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Spot bottlenecks, SLA breaches, and workload imbalance—then click into stuck orders instantly.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              className="text-sm outline-none bg-transparent"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <Users className="w-4 h-4 text-slate-500" />
            <select
              className="text-sm outline-none bg-transparent"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
            >
              <option value="all">All assignees</option>
              <option value="unassigned">Unassigned</option>
              {companyUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName || u.email || u.id}
                </option>
              ))}
            </select>
          </div>

          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <Layers className="w-4 h-4 text-slate-500" />
            <select
              className="text-sm outline-none bg-transparent"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {allStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              className="text-sm outline-none w-52 bg-transparent"
              placeholder="Search account, product, notes..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button
            type="button"
            onClick={() => setOnlyOpen((v) => !v)}
            className={`text-sm px-3 py-2 rounded-xl border transition ${
              onlyOpen
                ? "bg-blue-50 border-blue-200 text-blue-700"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {onlyOpen ? "Showing: Open Only" : "Showing: All"}
          </button>
        </div>
      </div>

      {!isAdmin && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">Limited view</p>
              <p className="text-sm text-amber-800 mt-1">
                Some bottleneck and team workload insights are admin-focused. You can still triage stuck orders.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card
          icon={Layers}
          title="Open sample orders"
          value={kpis.openCount}
          sub="Current workload in the pipeline"
          tone="blue"
        />
        <Card
          icon={AlertTriangle}
          title="Overdue delivery dates"
          value={kpis.overdueCount}
          sub="Requested date has passed (still open)"
          tone={kpis.overdueCount > 0 ? "amber" : "slate"}
        />
        <Card
          icon={TimerReset}
          title="Stuck orders"
          value={kpis.stuckCount}
          sub="Older than your per-status threshold"
          tone={kpis.stuckCount > 0 ? "red" : "slate"}
        />
        <Card
          icon={Clock}
          title="Median age in current status"
          value={fmtDuration(kpis.medAgeMs || 0)}
          sub="Helps identify systemic slowdowns"
          tone="slate"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Top accounts sampled</p>
              <p className="text-xs text-slate-500 mt-1">
                See which accounts are driving the most requests, and how much of that workload is still open.
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              {timeframe === "all" ? "All time" : `Last ${timeframe} days`}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                Created
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {activitySnapshot.created}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                Delivered
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {activitySnapshot.delivered}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                Completed
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {activitySnapshot.completed}
              </p>
            </div>
          </div>

          <div className="mt-4 h-72">
            {accountChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                No account data for the current filters.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={accountChartData}
                  layout="vertical"
                  margin={{ top: 6, right: 12, left: 28, bottom: 0 }}
                  barGap={6}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fill: "#475569", fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
                    contentStyle={{
                      borderRadius: 16,
                      border: "1px solid #cbd5e1",
                      boxShadow: "0 12px 28px rgba(15,23,42,0.12)",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
                  <Bar
                    dataKey="open"
                    name="Still open"
                    stackId="accounts"
                    fill="#93c5fd"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="completed"
                    name="Completed"
                    stackId="accounts"
                    fill="#34d399"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="converted"
                    name="Converted"
                    stackId="accounts"
                    fill="#8b5cf6"
                    radius={[0, 8, 8, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Status mix</p>
          <p className="text-xs text-slate-500 mt-1">
            A quick breakdown of the statuses in your current filtered view.
          </p>

          <div className="mt-4 h-44">
            {statusChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                No status data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={34}
                    outerRadius={58}
                    paddingAngle={2}
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 16, border: "1px solid #cbd5e1", boxShadow: "0 12px 28px rgba(15,23,42,0.12)" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="mt-3 space-y-2">
            {statusChartData.slice(0, 5).map((row, index) => (
              <div key={row.name} className="flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                  <span className="truncate text-slate-600">{row.name}</span>
                </div>
                <span className="font-semibold text-slate-900">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottleneck + status funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Status funnel</p>
              <p className="text-xs text-slate-500 mt-1">
                Where work is piling up right now (based on your filters).
              </p>
            </div>
            {bottleneck ? (
              <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Bottleneck: {bottleneck.status} • Med {fmtDuration(bottleneck.medAgeMs || 0)}
              </span>
            ) : null}
          </div>

          <div className="mt-4 space-y-3">
            {statusBuckets.length === 0 ? (
              <p className="text-sm text-slate-500">No orders match the current filters.</p>
            ) : (
              statusBuckets.map((s) => {
                const pct = Math.round(((s.count || 0) / maxBar) * 100);
                return (
                  <div key={s.status} className="flex items-center gap-3">
                    <div className="w-40 min-w-[10rem]">
                      <p className="text-sm text-slate-800 font-medium truncate">{s.status}</p>
                      <p className="text-xs text-slate-500">
                        {s.count} • Med age {fmtDuration(s.medAgeMs || 0)}
                      </p>
                    </div>
                    <div className="flex-1">
                      <div className="h-2.5 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                        <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="w-14 text-right text-xs text-slate-500">{pct}%</div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Hold-up thresholds</p>
          <p className="text-xs text-slate-500 mt-1">
            Define "stuck" by status (days). This affects the Stuck KPI + triage list.
          </p>

          <div className="mt-4 space-y-3">
            {editableStatuses.slice(0, 6).map((s) => {
              const cur = thresholds[s] ?? thresholds[keyify(s)] ?? getDefaultThresholdDays(s) ?? 3;
              return (
                <div key={s} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-700 truncate">{s}</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={cur}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setThresholds((prev) => ({
                        ...prev,
                        [s]: Number.isFinite(v) ? v : prev[s],
                      }));
                    }}
                    className="w-20 text-sm px-2 py-1 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              );
            })}
            {editableStatuses.length > 6 ? (
              <p className="text-[11px] text-slate-400 pt-1">
                (Showing first 6 statuses. You can add more defaults in the component later.)
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Stuck orders triage */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Stuck & overdue triage</p>
            <p className="text-xs text-slate-500 mt-1">
              Click an order to jump straight into edit mode and unblock it.
            </p>
          </div>
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600">
            Showing top {stuckOrders.length} by age
          </span>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Age</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2 pr-3">Requested</th>
                <th className="py-2 pr-3">Assigned</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {stuckOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-500">
                    No stuck or overdue orders found for the current filters.
                  </td>
                </tr>
              ) : (
                stuckOrders.map((r) => {
                  const age = fmtDuration(r._ageInStatusMs || 0);
                  const tone =
                    r._overdue && r._stuck
                      ? "bg-red-50"
                      : r._overdue
                      ? "bg-amber-50"
                      : r._stuck
                      ? "bg-red-50"
                      : "";

                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${tone}`}
                    >
                      <td className="py-3 pr-3 font-medium text-slate-900">{age}</td>
                      <td className="py-3 pr-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
                          {r._status}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-slate-700">
                        {r._accountName || "—"}
                      </td>
                      <td className="py-3 pr-3 text-slate-700">
                        {fmtDate(r._requestedDelivery)}
                      </td>
                      <td className="py-3 pr-3 text-slate-700">
                        {r._assignedToName || "Unassigned"}
                      </td>
                      <td className="py-3 pr-3 text-slate-700">{fmtDate(r._createdAt)}</td>
                      <td className="py-3 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof onSelectRequest === "function") onSelectRequest(r);
                          }}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-white bg-slate-50"
                        >
                          Open <ArrowUpRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Team workload (admin-friendly) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Workload by assignee</p>
          <p className="text-xs text-slate-500 mt-1">
            Quickly see who is overloaded and who has stuck/overdue items.
          </p>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Assignee</th>
                  <th className="py-2 pr-3">Open</th>
                  <th className="py-2 pr-3">Stuck</th>
                  <th className="py-2 pr-3">Overdue</th>
                  <th className="py-2 pr-3">Median age</th>
                </tr>
              </thead>
              <tbody>
                {perAssignee.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-slate-500">
                      No open items for the current filters.
                    </td>
                  </tr>
                ) : (
                  perAssignee.slice(0, 12).map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 pr-3 font-medium text-slate-900">{row.name}</td>
                      <td className="py-3 pr-3 text-slate-700">{row.openCount}</td>
                      <td className="py-3 pr-3 text-slate-700">
                        {row.stuckCount > 0 ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                            {row.stuckCount}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                            0
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-slate-700">
                        {row.overdueCount > 0 ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            {row.overdueCount}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                            0
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-slate-700">{fmtDuration(row.medAgeMs || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isAdmin ? (
            <div className="mt-3 text-xs text-slate-500">
              (Some workload insights may be less meaningful for non-admin users.)
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Layers className="w-4 h-4 text-violet-600" />
            Kit Usage
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Slim view of how often kits are used and how often kit-based requests convert.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Number of times kits have been used</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{kitUsage.usageCount}</p>
              <p className="mt-1 text-[11px] text-slate-500">Across {kitUsage.uniqueKitCount} unique kits in the current filters.</p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Kit requests converted to orders</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{kitUsage.conversionPct}%</p>
              <p className="mt-1 text-[11px] text-slate-500">{kitUsage.convertedCount} of {kitUsage.usageCount} kit-based requests converted.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            Process timing (best effort)
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Uses statusEnteredAt/statusExitedAt when available. Otherwise falls back.
          </p>

          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Median cycle time (closed)</p>
              <p className="text-lg font-semibold text-slate-900 mt-1">
                {fmtDuration(kpis.medCycleMs || 0)}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Avg cycle time (closed)</p>
              <p className="text-lg font-semibold text-slate-900 mt-1">
                {fmtDuration(kpis.avgCycleMs || 0)}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-xs font-medium text-slate-700">Where time is spent (top)</p>
              <div className="mt-3 space-y-2">
                {timeSpentByStatus.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    No status duration data yet. (Once status changes occur, this fills in.)
                  </p>
                ) : (
                  timeSpentByStatus.slice(0, 5).map((row) => (
                    <div key={row.statusKey} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-700 truncate">
                        {row.statusKey.replaceAll("_", " ")}
                      </span>
                      <span className="text-xs text-slate-900 font-medium">
                        Med {fmtDuration(row.medMs || 0)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAdmin ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Cost insights</p>
          <p className="text-xs text-slate-500 mt-1">
            Combine process bottlenecks with cost to spot expensive slowdowns.
          </p>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <SampleOrderCostTimeFrame companyId={companyId} sampleOrders={filtered} />
            </div>
            <div className="lg:col-span-1">
              <SampleOrderCostPerUser
                companyId={companyId}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                sampleOrders={filtered}
                companyUsers={companyUsers}
              />
            </div>
          </div>

          {companySettings?.sampleRequestLeadDays ? (
            <p className="text-[11px] text-slate-400 mt-3">
              FYI: Lead days configured: {companySettings.sampleRequestLeadDays}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SampleOrdersAnalytics;
